import type { TimestampFormat } from '@/contexts/TimestampFormatContext';

/**
 * Format-aware fixed column widths (px) for any table column that renders
 * `RelativeTimeWithTooltip` (org/personal Alerts "Last Seen"/"First Seen", Audit
 * Logs "Timestamp"). The width depends on the active per-user Timestamp Format:
 * the relative form is short, the absolute UTC form is long.
 *
 * Relative (150): only needs room for the header label + the sort/filter icons;
 * matches the pre-existing 150px default the alerts columns already used.
 *
 * Absolute (250): budgeted from the widest cell variant — the Audit Logs
 * Timestamp cell, which renders `showIcon=true`. With `px-3` cell padding (24px)
 * + a `w-3` Calendar icon (12px) + `gap-1` (4px), the inner text span gets
 * `250 - 40 = 210px`, which fits "<Month> D, YYYY at HH:MM:SS UTC" (~190-205px
 * at text-sm). Icon-less alerts cells have an even larger budget at the same
 * width. The inline truncate-containment span + opposite-format tooltip on
 * `RelativeTimeWithTooltip` remain the narrow-viewport / longest-month fallback.
 *
 * Lives in its own module (not the `RelativeTimeWithTooltip.tsx` component file)
 * so the helper can be a function without tripping the
 * `react-refresh/only-export-components` rule.
 */
export const TIMESTAMP_COLUMN_WIDTH_RELATIVE = 150;
export const TIMESTAMP_COLUMN_WIDTH_ABSOLUTE = 250;

const TIMESTAMP_COLUMN_WIDTH_BY_FORMAT: Record<TimestampFormat, number> = {
  relative: TIMESTAMP_COLUMN_WIDTH_RELATIVE,
  absolute: TIMESTAMP_COLUMN_WIDTH_ABSOLUTE,
};

/**
 * Resolve the fixed column width for the active Timestamp Format. Columns are
 * recreated when the format toggles, so the table picks up the new width.
 */
export function getTimestampColumnWidth(format: TimestampFormat): number {
  return TIMESTAMP_COLUMN_WIDTH_BY_FORMAT[format];
}
